import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DateFilter } from '@/components/DateFilter';
import { BranchFilter } from '@/components/filters/BranchFilter';
import { ExportButtons } from '@/components/ExportButtons';;
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, Play, Trash2, Download, Eye, Users, TrendingUp, FileText, Calendar, ChevronDown, Settings, Clock, AlertTriangle, Timer, Zap, Ban, ArrowDownUp, Globe, FileSpreadsheet, Wallet } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import SalaryStatusTab from '@/components/payroll/SalaryStatusTab';

export default function PayrollPage() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('process');
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const [payrollBranchId, setPayrollBranchId] = useState(null);  // iter880 — per-branch payroll runs
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [runsVisible, setRunsVisible] = useState(12);
  const [employees, setEmployees] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [showPayslips, setShowPayslips] = useState(false);
  const [summary, setSummary] = useState(null);

  // Rules
  const [rules, setRules] = useState(null);
  const [rulesLoading, setRulesLoading] = useState(false);

  // Preview
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewPeriod, setPreviewPeriod] = useState(new Date().toISOString().slice(0, 7));

  const fetchData = async () => {
    try {
      const [runsRes, empRes, summaryRes] = await Promise.all([
        api.get('/payroll/runs'),
        api.get('/employees'),
        api.get('/payroll/summary'),
      ]);
      setPayrollRuns(runsRes.data);
      setEmployees(empRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    setRulesLoading(true);
    try {
      const res = await api.get('/payroll/rules');
      setRules(res.data);
    } catch {} finally { setRulesLoading(false); }
  };

  const saveRules = async () => {
    try {
      const res = await api.put('/payroll/rules', rules);
      setRules(res.data);
      toast.success('Payroll rules saved');
    } catch { toast.error('Failed to save rules'); }
  };

  const fetchPreview = async () => {
    setPreviewLoading(true);
    try {
      const params = { period: previewPeriod };
      if (payrollBranchId) params.branch_id = payrollBranchId;
      const res = await api.get('/payroll/preview', { params });
      setPreview(res.data);
    } catch (err) { toast.error('Failed to generate preview'); } finally { setPreviewLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { if (tab === 'rules' && !rules) fetchRules(); }, [tab]);
  useEffect(() => { if (tab === 'preview') fetchPreview(); }, [tab, previewPeriod]);

  const handleProcessPayroll = async () => {
    if (!period) { toast.error('Please select a period'); return; }
    setProcessing(true);
    try {
      const body = { period, working_days_in_month: rules?.working_days_per_month || 22 };
      if (payrollBranchId) body.branch_id = payrollBranchId;
      const res = await api.post('/payroll/process', body);
      setLastRun(res.data);
      const n = res.data.loans_auto_deducted || 0;
      const amt = res.data.loans_auto_deducted_amount || 0;
      toast.success(
        `Payroll processed for ${res.data.employee_count} employees` +
        (n > 0 ? ` · ${cur()} ${amt.toLocaleString()} auto-deducted across ${n} loan installment${n > 1 ? 's' : ''}` : '')
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process payroll');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewPayslips = async (runId) => {
    try {
      const res = await api.get(`/payroll/${runId}/payslips`);
      setPayslips(res.data);
      setSelectedRun(payrollRuns.find(r => r.id === runId));
      setShowPayslips(true);
    } catch (err) { toast.error('Failed to load payslips'); }
  };

  const handleDeleteRun = async (runId) => {
    let impact;
    try {
      const res = await api.get(`/payroll/${runId}/delete-impact`);
      impact = res.data;
    } catch (err) {
      toast.error('Failed to load deletion impact');
      return;
    }

    let confirmMsg;
    let withLoanReversal = false;
    if (impact.has_loan_side_effects) {
      withLoanReversal = true;
      const lines = (impact.loan_reversals || []).map(r =>
        `• ${r.employee_name || r.employee_id}: restore ${cur()} ${Number(r.amount).toLocaleString()} `
        + `(loan ${r.loan_after?.status}, remaining ${cur()} ${Number(r.loan_after?.remaining_balance).toLocaleString()}, `
        + `paid ${r.loan_after?.paid_installments})`
      ).join('\n');
      confirmMsg =
        `${impact.warning}\n\n`
        + `Period: ${impact.period}\n`
        + `Payslips to remove: ${impact.payslip_count}\n`
        + `Loan installments to reverse: ${impact.loan_reversals.length}\n${lines}\n\n`
        + `Type OK to confirm this will restore the affected employee loan balance(s).`;
    } else {
      confirmMsg = `Delete this payroll run (${impact.period}) and its ${impact.payslip_count} payslips?`;
    }

    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await api.delete(`/payroll/${runId}`, {
        params: withLoanReversal
          ? { confirm_loan_reversal: true, reason: 'mistaken payroll run' }
          : { reason: 'mistaken payroll run' },
      });
      const rev = res.data?.loan_installments_reversed?.length || 0;
      toast.success(
        `Payroll run deleted · ${res.data?.payslips_deleted ?? ''} payslips removed`
        + (rev > 0 ? ` · ${rev} loan installment${rev > 1 ? 's' : ''} reversed` : '')
      );
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Failed to delete'); }
  };

  const handleExportWPS = async (runId, bankFormat = 'standard') => {
    try {
      const res = await api.get(`/payroll/${runId}/wps/${bankFormat}`);
      const { filename, content, summary: s, format_label } = res.data;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${format_label || 'WPS'} file exported: ${s.total_employees} employees, ${s.total_net_salary.toLocaleString()} ${s.currency}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to export WPS'); }
  };

  const handleDownloadPayslipPdf = async (runId, payslipId, empName) => {
    try {
      const res = await api.get(`/payroll/${runId}/payslips/${payslipId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `payslip_${empName.replace(/\s/g, '_')}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`PDF downloaded for ${empName}`);
    } catch { toast.error('Failed to download PDF'); }
  };

  const handleDownloadAllPdfs = async (runId, period) => {
    try {
      toast.info('Generating PDFs...');
      const res = await api.get(`/payroll/${runId}/pdf-all`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `payslips_${period}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success('All payslip PDFs downloaded');
    } catch { toast.error('Failed to download PDFs'); }
  };

  const WPS_BANKS = [
    { id: 'standard', label: 'Standard SIF' }, { id: 'alrajhi', label: 'Al Rajhi Bank' },
    { id: 'sabb', label: 'SABB (HSBC)' }, { id: 'snb', label: 'SNB (Al Ahli)' },
    { id: 'riyad', label: 'Riyad Bank' }, { id: 'alinma', label: 'Alinma Bank' },
  ];

  const formatPeriod = (p) => {
    if (!p) return '-';
    const [y, m] = p.split('-');
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    return `${months[parseInt(m, 10) - 1] || m} ${y}`;
  };

  const activeEmployees = employees.filter(e => e.status === 'active' && e.salary > 0);
  const totalPayroll = activeEmployees.reduce((s, e) => s + (e.salary || 0), 0);

  const updateRule = (key, field, value) => {
    setRules(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payroll-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <DollarSign size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground dark:text-white" data-testid="page-title">{t('payroll_title') || 'Payroll Management'}</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Process payroll, configure rules & preview adjustments</p>
            
            <DateFilter onFilterChange={setDateFilter} />
            {/* iter880 — Multi-branch payroll runs. Passed to the Preview /
                Process flows via `branchId` state below. */}
            <BranchFilter value={payrollBranchId} onChange={setPayrollBranchId} width="w-40" testid="payroll-branch" />
            <ExportButtons dataType="salary_payments" label="Payroll" startDate={dateFilter?.start} endDate={dateFilter?.end} /></div>
          </div>
          <PageTour storageKey="tour_payroll" steps={[
            { target: '[data-testid="tab-process"]', title: 'Process Payroll', content: 'Select a pay period and run payroll for all active employees. View history of past runs.' },
            { target: '[data-testid="tab-rules"]', title: 'Payroll Rules', content: 'Configure allowances, deductions, overtime rates, and other payroll calculation rules.' },
            { target: '[data-testid="tab-preview"]', title: 'Preview', content: 'Preview payroll calculations before processing to catch any issues.' },
          ]} />
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white dark:bg-foreground" data-testid="stat-active-employees">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Active Employees</p>
              <p className="text-2xl font-bold font-outfit text-info mt-1">{activeEmployees.length}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white dark:bg-foreground" data-testid="stat-monthly-payroll">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-success uppercase tracking-[0.08em]">Monthly Payroll</p>
              <p className="text-2xl font-bold font-outfit text-success mt-1">{cur()} {totalPayroll.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white dark:bg-foreground" data-testid="stat-total-runs">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-warning uppercase tracking-[0.08em]">Payroll Runs</p>
              <p className="text-2xl font-bold font-outfit text-warning mt-1">{summary?.total_runs || 0}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white dark:bg-foreground" data-testid="stat-last-run">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Last Processed</p>
              <p className="text-2xl font-bold font-outfit text-purple-600 mt-1">{formatPeriod(summary?.last_run?.period) || '-'}</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="payroll-tabs">
            <TabsTrigger value="process" data-testid="tab-process-2"><Play size={14} className="mr-1.5" />Process</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules-2"><Settings size={14} className="mr-1.5" />Rules</TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview-2"><Eye size={14} className="mr-1.5" />Preview</TabsTrigger>
            <TabsTrigger value="status" data-testid="tab-salary-status"><Wallet size={14} className="mr-1.5" />Salary Status</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports"><FileText size={14} className="mr-1.5" />Reports</TabsTrigger>
          </TabsList>

          {/* PROCESS TAB */}
          <TabsContent value="process">
            <div className="space-y-4">
              <Card data-testid="process-payroll-card">
                <CardHeader className="pt-5 px-6 pb-0">
                  <CardTitle className="text-[15px] font-outfit font-semibold text-foreground dark:text-white flex items-center gap-2"><Play size={16} className="text-primary" /> Process Payroll</CardTitle>
                </CardHeader>
                <CardContent className="px-6 pt-4">
                  <div className="flex items-end gap-4">
                    <div className="flex-1 max-w-xs">
                      <Label className="text-[12px] font-medium text-muted-foreground">Period (YYYY-MM)</Label>
                      <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="payroll-period-input" className="mt-1.5 rounded-xl" />
                    </div>
                    <Button onClick={handleProcessPayroll} disabled={processing} data-testid="process-payroll-btn" className="rounded-xl h-10 text-[13px]">
                      <Play size={14} className="mr-1.5" />{processing ? 'Processing...' : 'Run Payroll'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Generates payslips for {activeEmployees.length} active employees using configured rules.</p>
                </CardContent>
              </Card>

              {/* iter805 — loan auto-deduction summary for the last run */}
              {lastRun && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900 p-4" data-testid="payroll-run-summary">
                  <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                    <Play size={14} /> Payroll run complete · {formatPeriod(lastRun.period)}
                  </div>
                  <p className="text-[13px] text-emerald-700 dark:text-emerald-400 mt-1" data-testid="loan-deduction-summary-line">
                    {lastRun.employee_count} employees · Net {cur()} {(lastRun.total_net || 0).toLocaleString()}
                    {(lastRun.loans_auto_deducted || 0) > 0
                      ? ` · ${cur()} ${(lastRun.loans_auto_deducted_amount || 0).toLocaleString()} auto-deducted across ${lastRun.loans_auto_deducted} loan installment${lastRun.loans_auto_deducted > 1 ? 's' : ''}`
                      : ' · no loan installments due'}
                  </p>
                  {(lastRun.loan_deductions || []).length > 0 && (
                    <ul className="mt-2 space-y-1 text-[12px] text-emerald-700 dark:text-emerald-400">
                      {lastRun.loan_deductions.map((d, i) => (
                        <li key={i} className="flex items-center justify-between max-w-md" data-testid={`loan-deduction-line-${i}`}>
                          <span>{d.employee_name}</span>
                          <span className="tabular-nums">
                            {cur()} {(d.amount || 0).toLocaleString()}
                            {d.cleared ? <span className="ml-1.5 text-[10px] font-semibold uppercase text-emerald-600">· cleared</span>
                                       : <span className="ml-1.5 text-[10px] text-emerald-600/70">· {cur()} {(d.remaining_balance || 0).toLocaleString()} left</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Payroll Runs */}
              <Card>
                <CardHeader><CardTitle className="text-base">Payroll History</CardTitle></CardHeader>
                <CardContent>
                  {payrollRuns.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">No payroll runs yet. Process your first payroll above.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="payroll-runs-table">
                        <thead><tr className="border-b text-left text-xs text-muted-foreground">
                          <th className="pb-2 pr-4">Period</th><th className="pb-2 pr-4">Employees</th><th className="pb-2 pr-4">Gross Total</th><th className="pb-2 pr-4">Deductions</th><th className="pb-2 pr-4">Net Total</th><th className="pb-2 pr-4">Status</th><th className="pb-2">Actions</th>
                        </tr></thead>
                        <tbody>
                          {payrollRuns.slice(0, runsVisible).map((run) => (
                            <tr key={run.id} className="border-b last:border-0 hover:bg-muted/50 dark:hover:bg-muted/50" data-testid={`payroll-run-${run.id}`}>
                              <td className="py-2.5 pr-4 font-medium dark:text-white">{formatPeriod(run.period)}</td>
                              <td className="py-2.5 pr-4">{run.employee_count}</td>
                              <td className="py-2.5 pr-4">{run.total_gross?.toLocaleString()}</td>
                              <td className="py-2.5 pr-4 text-error">{run.total_deductions?.toLocaleString()}</td>
                              <td className="py-2.5 pr-4 font-medium text-success">{run.total_net?.toLocaleString()}</td>
                              <td className="py-2.5 pr-4"><Badge variant={run.status === 'completed' ? 'default' : 'secondary'}>{run.status}</Badge></td>
                              <td className="py-2.5">
                                <div className="flex gap-1.5">
                                  <Button size="sm" variant="ghost" onClick={() => handleViewPayslips(run.id)} data-testid={`view-payslips-${run.id}`}><Eye size={14} /></Button>
                                  <Button size="sm" variant="ghost" title="Download All PDFs" onClick={() => handleDownloadAllPdfs(run.id, run.period)} data-testid={`pdf-all-${run.id}`}><FileText size={14} /></Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="sm" variant="ghost" data-testid={`wps-export-${run.id}`} title="Export WPS"><Download size={14} /><ChevronDown size={10} className="ml-0.5" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {WPS_BANKS.map(b => (<DropdownMenuItem key={b.id} onClick={() => handleExportWPS(run.id, b.id)} className="text-xs">{b.label}</DropdownMenuItem>))}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                  <Button size="sm" variant="ghost" className="text-error" onClick={() => handleDeleteRun(run.id)} data-testid={`delete-run-${run.id}`}><Trash2 size={14} /></Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {payrollRuns.length > runsVisible && (
                        <div className="text-center mt-3">
                          <Button variant="outline" className="rounded-xl" onClick={() => setRunsVisible(c => c + 12)} data-testid="load-more-payroll-runs-btn">
                            Load More ({Math.min(runsVisible, payrollRuns.length)} of {payrollRuns.length})
                          </Button>
                          <Button variant="ghost" className="rounded-xl ml-2" onClick={() => setRunsVisible(payrollRuns.length)} data-testid="show-all-payroll-runs-btn">
                            Show All
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* RULES TAB */}
          <TabsContent value="rules">
            {!rules ? (
              <div className="text-center py-12 text-muted-foreground"><Settings size={24} className="animate-spin mx-auto mb-2" />Loading rules...</div>
            ) : (
              <div className="space-y-4" data-testid="rules-section">
                <div className="p-3 bg-info/10 border border-info/30 rounded-xl text-xs text-info dark:bg-blue-900/10 dark:border-blue-800 dark:text-blue-400">
                  Toggle each rule on/off. Only enabled rules will apply when processing payroll. Disabled rules have zero impact.
                </div>

                {/* Working Days */}
                <Card data-testid="working-days-card">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold dark:text-white">Working Days per Month</p>
                      <p className="text-[10px] text-muted-foreground">Used for daily rate calculation</p>
                    </div>
                    <Input type="number" min={1} max={31} className="w-20 h-8 text-center text-sm" data-testid="working-days-input"
                      value={rules.working_days_per_month || 22}
                      onChange={(e) => setRules(prev => ({ ...prev, working_days_per_month: parseInt(e.target.value) || 22 }))} />
                  </CardContent>
                </Card>

                {/* Late Deduction */}
                <Card data-testid="late-deduction-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock size={16} className="text-warning" />
                        <CardTitle className="text-sm font-outfit">Late Arrival Deduction</CardTitle>
                      </div>
                      <Switch checked={rules.late_deduction?.enabled || false} onCheckedChange={(v) => updateRule('late_deduction', 'enabled', v)} data-testid="late-deduction-toggle" />
                    </div>
                  </CardHeader>
                  {rules.late_deduction?.enabled && (
                    <CardContent className="space-y-3 pt-0">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div>
                          <Label className="text-xs">Mode</Label>
                          <Select value={rules.late_deduction?.mode || 'per_minute'} onValueChange={(v) => updateRule('late_deduction', 'mode', v)}>
                            <SelectTrigger className="h-8 mt-1 text-xs" data-testid="late-mode-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="per_minute">Per Minute (hourly rate)</SelectItem>
                              <SelectItem value="fixed">Fixed per Incident</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {rules.late_deduction?.mode === 'fixed' ? (
                          <div>
                            <Label className="text-xs">Fixed Amount per Incident</Label>
                            <Input type="number" min={0} step="0.01" className="h-8 mt-1 text-xs" data-testid="late-fixed-amount"
                              value={rules.late_deduction?.fixed_per_incident || 0}
                              onChange={(e) => updateRule('late_deduction', 'fixed_per_incident', parseFloat(e.target.value) || 0)} />
                          </div>
                        ) : (
                          <div>
                            <Label className="text-xs">Amount per Minute (0 = auto)</Label>
                            <Input type="number" min={0} step="0.01" className="h-8 mt-1 text-xs" data-testid="late-per-minute"
                              value={rules.late_deduction?.amount_per_minute || 0}
                              onChange={(e) => updateRule('late_deduction', 'amount_per_minute', parseFloat(e.target.value) || 0)} />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Grace Period (min)</Label>
                          <Input type="number" min={0} max={60} className="h-8 mt-1 text-xs" data-testid="late-grace"
                            value={rules.late_deduction?.grace_minutes ?? 5}
                            onChange={(e) => updateRule('late_deduction', 'grace_minutes', parseInt(e.target.value) || 0)} />
                        </div>
                        <div>
                          <Label className="text-xs">Min Late Days to Deduct</Label>
                          <Input type="number" min={0} className="h-8 mt-1 text-xs" data-testid="late-min-days"
                            value={rules.late_deduction?.min_late_days || 0}
                            onChange={(e) => updateRule('late_deduction', 'min_late_days', parseInt(e.target.value) || 0)} />
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Absence Deduction */}
                <Card data-testid="absence-deduction-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Ban size={16} className="text-error" />
                        <CardTitle className="text-sm font-outfit">Absence Deduction</CardTitle>
                      </div>
                      <Switch checked={rules.absence_deduction?.enabled || false} onCheckedChange={(v) => updateRule('absence_deduction', 'enabled', v)} data-testid="absence-deduction-toggle" />
                    </div>
                  </CardHeader>
                  {rules.absence_deduction?.enabled && (
                    <CardContent className="pt-0">
                      <div className="max-w-xs">
                        <Label className="text-xs">Daily Rate Multiplier</Label>
                        <p className="text-[10px] text-muted-foreground mb-1">1.0 = one day's pay, 1.5 = 1.5x daily rate per absence</p>
                        <Input type="number" min={0.5} max={3} step="0.1" className="h-8 text-xs" data-testid="absence-multiplier"
                          value={rules.absence_deduction?.daily_rate_multiplier ?? 1}
                          onChange={(e) => updateRule('absence_deduction', 'daily_rate_multiplier', parseFloat(e.target.value) || 1)} />
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Half-Day Deduction */}
                <Card data-testid="halfday-deduction-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Timer size={16} className="text-orange-500" />
                        <CardTitle className="text-sm font-outfit">Half-Day Deduction</CardTitle>
                      </div>
                      <Switch checked={rules.half_day_deduction?.enabled || false} onCheckedChange={(v) => updateRule('half_day_deduction', 'enabled', v)} data-testid="halfday-deduction-toggle" />
                    </div>
                  </CardHeader>
                  {rules.half_day_deduction?.enabled && (
                    <CardContent className="pt-0">
                      <div className="max-w-xs">
                        <Label className="text-xs">Deduction Percentage</Label>
                        <p className="text-[10px] text-muted-foreground mb-1">% of daily rate deducted for less than 4 hours worked</p>
                        <Input type="number" min={10} max={100} className="h-8 text-xs" data-testid="halfday-percentage"
                          value={rules.half_day_deduction?.deduction_percentage ?? 50}
                          onChange={(e) => updateRule('half_day_deduction', 'deduction_percentage', parseInt(e.target.value) || 50)} />
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Overtime Bonus */}
                <Card data-testid="overtime-bonus-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap size={16} className="text-success" />
                        <CardTitle className="text-sm font-outfit">Overtime Bonus</CardTitle>
                      </div>
                      <Switch checked={rules.overtime_bonus?.enabled || false} onCheckedChange={(v) => updateRule('overtime_bonus', 'enabled', v)} data-testid="overtime-bonus-toggle" />
                    </div>
                  </CardHeader>
                  {rules.overtime_bonus?.enabled && (
                    <CardContent className="pt-0">
                      <div className="max-w-xs">
                        <Label className="text-xs">Rate Multiplier</Label>
                        <p className="text-[10px] text-muted-foreground mb-1">1.5 = time-and-a-half, 2.0 = double time</p>
                        <Input type="number" min={1} max={5} step="0.1" className="h-8 text-xs" data-testid="overtime-multiplier"
                          value={rules.overtime_bonus?.rate_multiplier ?? 1.5}
                          onChange={(e) => updateRule('overtime_bonus', 'rate_multiplier', parseFloat(e.target.value) || 1.5)} />
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Consecutive Absence Penalty */}
                <Card data-testid="consecutive-penalty-card">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={16} className="text-error" />
                        <CardTitle className="text-sm font-outfit">Consecutive Absence Penalty</CardTitle>
                      </div>
                      <Switch checked={rules.consecutive_absence_penalty?.enabled || false} onCheckedChange={(v) => updateRule('consecutive_absence_penalty', 'enabled', v)} data-testid="consecutive-penalty-toggle" />
                    </div>
                  </CardHeader>
                  {rules.consecutive_absence_penalty?.enabled && (
                    <CardContent className="space-y-3 pt-0">
                      <div className="grid grid-cols-2 gap-3 max-w-md">
                        <div>
                          <Label className="text-xs">Threshold (days)</Label>
                          <Input type="number" min={1} max={30} className="h-8 mt-1 text-xs" data-testid="consecutive-threshold"
                            value={rules.consecutive_absence_penalty?.threshold_days ?? 3}
                            onChange={(e) => updateRule('consecutive_absence_penalty', 'threshold_days', parseInt(e.target.value) || 3)} />
                        </div>
                        <div>
                          <Label className="text-xs">Extra Deduction/Day (0 = auto)</Label>
                          <Input type="number" min={0} step="0.01" className="h-8 mt-1 text-xs" data-testid="consecutive-extra"
                            value={rules.consecutive_absence_penalty?.extra_deduction_per_day ?? 0}
                            onChange={(e) => updateRule('consecutive_absence_penalty', 'extra_deduction_per_day', parseFloat(e.target.value) || 0)} />
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>

                <div className="flex justify-end">
                  <Button onClick={saveRules} className="bg-success hover:bg-emerald-700" data-testid="save-rules-btn">
                    Save Payroll Rules
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* PREVIEW TAB */}
          <TabsContent value="preview">
            <div className="space-y-4" data-testid="preview-section">
              <div className="flex items-center gap-3">
                <div>
                  <Label className="text-xs">Preview Period</Label>
                  <Input type="month" value={previewPeriod} onChange={(e) => setPreviewPeriod(e.target.value)} className="h-8 mt-1 text-xs w-40" data-testid="preview-period-input" />
                </div>
                <Button variant="outline" size="sm" className="mt-5" onClick={fetchPreview} data-testid="refresh-preview-btn">
                  <ArrowDownUp size={14} className="mr-1.5" />Refresh
                </Button>
              </div>

              {previewLoading ? (
                <div className="text-center py-12 text-muted-foreground"><DollarSign size={24} className="animate-spin mx-auto mb-2" />Calculating...</div>
              ) : preview ? (
                <>
                  {/* Preview Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="preview-summary">
                    <Card className="border-l-4 border-l-blue-500"><CardContent className="pt-3 pb-2">
                      <div className="text-xs text-muted-foreground">Employees</div>
                      <div className="text-xl font-bold dark:text-white" data-testid="preview-emp-count">{preview.summary?.total_employees || 0}</div>
                    </CardContent></Card>
                    <Card className="border-l-4 border-l-red-500"><CardContent className="pt-3 pb-2">
                      <div className="text-xs text-muted-foreground">Total Deductions</div>
                      <div className="text-xl font-bold text-error" data-testid="preview-total-deductions">-{preview.summary?.total_deductions?.toLocaleString()}</div>
                    </CardContent></Card>
                    <Card className="border-l-4 border-l-emerald-500"><CardContent className="pt-3 pb-2">
                      <div className="text-xs text-muted-foreground">Total Bonuses</div>
                      <div className="text-xl font-bold text-success" data-testid="preview-total-bonuses">+{preview.summary?.total_bonuses?.toLocaleString()}</div>
                    </CardContent></Card>
                    <Card className="border-l-4 border-l-violet-500"><CardContent className="pt-3 pb-2">
                      <div className="text-xs text-muted-foreground">Net Impact</div>
                      <div className={`text-xl font-bold ${(preview.summary?.net_impact || 0) >= 0 ? 'text-success' : 'text-error'}`} data-testid="preview-net-impact">{preview.summary?.net_impact >= 0 ? '+' : ''}{preview.summary?.net_impact?.toLocaleString()}</div>
                    </CardContent></Card>
                  </div>

                  {/* Active Rules */}
                  {preview.rules_applied && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(preview.rules_applied).filter(([k, v]) => typeof v === 'object' && v?.enabled).map(([k]) => (
                        <Badge key={k} variant="outline" className="text-[10px]">{k.replace(/_/g, ' ')}</Badge>
                      ))}
                      {!Object.values(preview.rules_applied).some(v => typeof v === 'object' && v?.enabled) && (
                        <p className="text-xs text-muted-foreground">No deduction rules enabled. Go to Rules tab to configure.</p>
                      )}
                    </div>
                  )}

                  {/* Preview Table */}
                  <Card data-testid="preview-table-card">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Employee Adjustments — {formatPeriod(previewPeriod)}</CardTitle></CardHeader>
                    <CardContent>
                      {(preview.employees || []).length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground text-sm">No employees with salary found.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs" data-testid="preview-table">
                            <thead><tr className="border-b text-left text-[10px] text-muted-foreground">
                              <th className="pb-2 pr-3">Employee</th>
                              <th className="pb-2 pr-3 text-right">Salary</th>
                              <th className="pb-2 pr-3 text-center">Days</th>
                              <th className="pb-2 pr-3 text-center">Absent</th>
                              <th className="pb-2 pr-3 text-center">Late</th>
                              <th className="pb-2 pr-3 text-center">OT Hrs</th>
                              <th className="pb-2 pr-3 text-right">Late Ded</th>
                              <th className="pb-2 pr-3 text-right">Absence Ded</th>
                              <th className="pb-2 pr-3 text-right">OT Bonus</th>
                              <th className="pb-2 text-right">Net Adj</th>
                            </tr></thead>
                            <tbody>
                              {preview.employees.map((e) => (
                                <tr key={e.employee_id} className="border-b last:border-0 hover:bg-muted/50 dark:hover:bg-muted/50" data-testid={`preview-row-${e.employee_id}`}>
                                  <td className="py-2 pr-3 font-medium dark:text-white">{e.employee_name}</td>
                                  <td className="py-2 pr-3 text-right">{e.salary?.toLocaleString()}</td>
                                  <td className="py-2 pr-3 text-center">{e.working_days}</td>
                                  <td className="py-2 pr-3 text-center">{e.absent_days > 0 ? <span className="text-error">{e.absent_days}</span> : '-'}</td>
                                  <td className="py-2 pr-3 text-center">{e.late_days > 0 ? <span className="text-warning">{e.late_days}</span> : '-'}</td>
                                  <td className="py-2 pr-3 text-center">{e.overtime_hours > 0 ? e.overtime_hours : '-'}</td>
                                  <td className="py-2 pr-3 text-right">{e.late_deduction > 0 ? <span className="text-error">-{e.late_deduction}</span> : '-'}</td>
                                  <td className="py-2 pr-3 text-right">{e.absent_deduction > 0 ? <span className="text-error">-{e.absent_deduction}</span> : '-'}</td>
                                  <td className="py-2 pr-3 text-right">{e.overtime_bonus > 0 ? <span className="text-success">+{e.overtime_bonus}</span> : '-'}</td>
                                  <td className="py-2 text-right font-medium">
                                    <span className={e.net_adjustment >= 0 ? 'text-success' : 'text-error'}>
                                      {e.net_adjustment >= 0 ? '+' : ''}{e.net_adjustment}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : null}
            </div>
          </TabsContent>

          {/* SALARY STATUS TAB — loan/advance deduction alerts */}
          <TabsContent value="status">
            <SalaryStatusTab />
          </TabsContent>

          {/* REPORTS TAB — country-aware regulatory & bank exports */}
          <TabsContent value="reports">
            <PayrollReportsTab payrollRuns={payrollRuns} />
          </TabsContent>
        </Tabs>

        {/* Payslips Dialog */}
        <Dialog open={showPayslips} onOpenChange={setShowPayslips}>
          <DialogContent className="max-w-4xl max-h-[85dvh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="payslips-dialog-title">Payslips - {formatPeriod(selectedRun?.period)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {payslips.map((slip) => (
                <Card key={slip.id} className="border" data-testid={`payslip-${slip.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-sm dark:text-white">{slip.employee_name}</div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => handleDownloadPayslipPdf(selectedRun?.id, slip.id, slip.employee_name)} data-testid={`pdf-payslip-${slip.id}`}><FileText size={10} className="mr-1" />PDF</Button>
                        <Badge variant="outline" className="text-xs">Net: {slip.net_salary?.toLocaleString()}</Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-muted-foreground">
                      <div>Basic: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.basic_salary?.toLocaleString()}</span></div>
                      <div>Housing: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.housing_allowance?.toLocaleString()}</span></div>
                      <div>Transport: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.transport_allowance?.toLocaleString()}</span></div>
                      <div>Overtime: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.overtime_pay?.toLocaleString()}</span></div>
                      <div>Gross: <span className="text-success font-medium">{slip.gross_salary?.toLocaleString()}</span></div>
                      <div>{slip.social_insurance_name || 'GOSI'}: <span className="text-error font-medium">-{slip.social_insurance_deduction?.toLocaleString() || slip.gosi_deduction?.toLocaleString()}</span></div>
                      <div>Late: <span className="text-error font-medium">-{slip.late_deduction?.toLocaleString()}</span></div>
                      <div>Absent: <span className="text-error font-medium">-{slip.absent_deduction?.toLocaleString()}</span></div>
                      {slip.half_day_deduction > 0 && <div>Half-Day: <span className="text-error font-medium">-{slip.half_day_deduction?.toLocaleString()}</span></div>}
                      {slip.consecutive_penalty > 0 && <div>Consec. Penalty: <span className="text-error font-medium">-{slip.consecutive_penalty?.toLocaleString()}</span></div>}
                      {slip.unpaid_leave_deduction > 0 && <div data-testid={`payslip-unpaid-leave-${slip.id}`}>Unpaid Leave ({slip.unpaid_leave_days}d): <span className="text-error font-medium">-{slip.unpaid_leave_deduction?.toLocaleString()}</span></div>}
                      <div>Loan: <span className="text-error font-medium">-{slip.loan_deduction?.toLocaleString()}</span></div>
                      <div>Days Worked: <span className="text-foreground dark:text-muted-foreground font-medium">{slip.working_days}</span></div>
                    </div>
                    {(slip.salary_tiers || []).length > 1 && (
                      <div className="mt-2 text-[11px] text-info bg-info/10 border border-info/20 rounded px-2 py-1" data-testid={`payslip-prorata-${slip.id}`}>
                        Mid-month salary change (pro-rated): {slip.salary_tiers.map((t, i) => `days ${t.day_start}–${t.day_end} @ ${Number(t.salary).toLocaleString()}`).join(' · ')} = {slip.monthly_salary?.toLocaleString()}
                      </div>
                    )}
                    <div className="mt-2 pt-2 border-t text-xs flex items-center justify-between flex-wrap gap-1" data-testid={`payslip-netpay-${slip.id}`}>
                      <span className="text-muted-foreground">Base Salary {slip.gross_salary?.toLocaleString()} − Deductions {slip.total_deductions?.toLocaleString()}{slip.unpaid_leave_deduction > 0 ? ' (incl. Unpaid Leave)' : ''}</span>
                      <span className="font-bold text-success">Net Pay: {slip.net_salary?.toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}


/* ════════════════════════════════════════════════════════════════════
 * Payroll Reports Tab — country-aware regulatory exports
 * ════════════════════════════════════════════════════════════════════ */

const COUNTRIES = [
  { code: 'SA', label: '🇸🇦  Saudi Arabia' },
  { code: 'AE', label: '🇦🇪  United Arab Emirates' },
  { code: 'BH', label: '🇧🇭  Bahrain' },
  { code: 'KW', label: '🇰🇼  Kuwait' },
  { code: 'OM', label: '🇴🇲  Oman' },
  { code: 'QA', label: '🇶🇦  Qatar' },
];

const WPS_BANKS_SA = [
  { id: 'alrajhi', label: 'Al Rajhi Bank' },
  { id: 'snb', label: 'SNB (Al Ahli)' },
  { id: 'sabb', label: 'SABB (HSBC)' },
  { id: 'riyad', label: 'Riyad Bank' },
  { id: 'alinma', label: 'Alinma Bank' },
  { id: 'standard', label: 'Standard SIF' },
];

function downloadFile(filename, content, mime = 'text/csv') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function PayrollReportsTab({ payrollRuns }) {
  const [country, setCountry] = useState(() => localStorage.getItem('payroll_reports_country') || 'SA');
  const [catalog, setCatalog] = useState(null);
  const [loadingCat, setLoadingCat] = useState(false);
  const [selectedRun, setSelectedRun] = useState(payrollRuns?.[0]?.id || '');
  const [selectedBank, setSelectedBank] = useState('alrajhi');
  const [lastSummary, setLastSummary] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    setLoadingCat(true);
    api.get(`/payroll/report-catalog?country=${country}`).then(r => setCatalog(r.data)).finally(() => setLoadingCat(false));
    localStorage.setItem('payroll_reports_country', country);
  }, [country]);

  useEffect(() => {
    if (!selectedRun && payrollRuns?.length) setSelectedRun(payrollRuns[0].id);
  }, [payrollRuns, selectedRun]);

  const exportReport = async (report) => {
    if (!selectedRun) { toast.error('Select a payroll run first'); return; }
    const key = `${report.id}-${report.id === 'wps' ? selectedBank : ''}`;
    setBusyKey(key);
    try {
      let endpoint;
      if (report.id === 'wps')        endpoint = `/payroll/${selectedRun}/wps/${selectedBank}`;
      else if (report.id === 'gosi')  endpoint = `/payroll/${selectedRun}/gosi`;
      else if (report.id === 'mudad') endpoint = `/payroll/${selectedRun}/mudad`;
      else if (report.id === 'mol')   endpoint = `/payroll/${selectedRun}/mol`;
      else if (report.id === 'zatca') endpoint = `/payroll/${selectedRun}/zatca`;
      else if (report.id === 'payslip') { toast.info('PDF payslips — use the download button on each run row.'); setBusyKey(null); return; }
      else { toast.info(`${report.label}: coming soon.`); setBusyKey(null); return; }
      const res = await api.get(endpoint);
      downloadFile(res.data.filename, res.data.content);
      setLastSummary({ report: report.label, ...res.data.summary });
      toast.success(`${report.label} exported · ${res.data.summary?.employees || '-'} employees`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to export ${report.label}`);
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1"><Globe size={12} /> Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="report-country"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground mt-1">Changes the regulatory report set</p>
            </div>
            <div>
              <Label className="text-xs">Payroll Run</Label>
              <Select value={selectedRun} onValueChange={setSelectedRun}>
                <SelectTrigger data-testid="report-run"><SelectValue placeholder="Pick a run" /></SelectTrigger>
                <SelectContent>
                  {(payrollRuns || []).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.period} · {r.employee_count || 0} emps · {cur()} {(r.total_net_pay || 0).toLocaleString()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!payrollRuns || !payrollRuns.length) && <p className="text-[10px] text-warning mt-1">No payroll runs yet. Run one first in the Process tab.</p>}
            </div>
            {country === 'SA' && (
              <div>
                <Label className="text-xs">WPS Bank Format</Label>
                <Select value={selectedBank} onValueChange={setSelectedBank}>
                  <SelectTrigger data-testid="report-bank"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WPS_BANKS_SA.map(b => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {lastSummary && (
        <Card className="border-success/40 bg-success/5">
          <CardContent className="p-3 text-xs">
            <span className="font-semibold text-success">Exported:</span> {lastSummary.report} · {lastSummary.employees ?? '-'} employees · {lastSummary.period || ''} · total {cur()} {(lastSummary.total_net_salary || lastSummary.total || lastSummary.total_net || 0).toLocaleString()}
          </CardContent>
        </Card>
      )}

      {loadingCat ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground text-sm">Loading {country} reports…</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {(catalog?.reports || []).map(r => {
            const busy = busyKey?.startsWith(r.id);
            return (
              <Card key={r.id} className="border hover:border-primary/40 hover:shadow-md transition-all" data-testid={`report-card-${r.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileSpreadsheet size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{r.label}</p>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{r.format} · {r.id}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2 min-h-[32px]">{r.description}</p>
                  <Button
                    size="sm"
                    className="w-full rounded-lg"
                    onClick={() => exportReport(r)}
                    disabled={busy || !selectedRun}
                    data-testid={`report-export-${r.id}`}
                  >
                    {busy ? (<><Clock size={13} className="mr-1 animate-spin" /> Exporting…</>) : (<><Download size={13} className="mr-1" /> Export</>)}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {catalog && catalog.reports.length === 0 && (
        <Card className="border-warning/40 bg-warning/5">
          <CardContent className="p-4 text-xs text-warning">No regulatory reports configured for {catalog.country_label}. Contact support to add one.</CardContent>
        </Card>
      )}
    </div>
  );
}
